import * as React from 'react'

import { Button } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import TextArea from 'modules/core/components/Inputs/TextArea'
import Modal from 'modules/core/components/Modal'
import { useUnsafeEffect } from 'modules/core/hooks/useEffect'
import asyncAction from 'modules/core/lib/asyncAction'
import i18n from 'modules/core/lib/i18n'
import { employeeUser } from 'modules/core/store/collections/users'

import type Employee from 'modules/employees/store/models/Employee'
import { useStatusAuthorizations } from 'modules/payroll_bundle/lib/statusAuthorizationsContext'
import observations from 'modules/payroll_bundle/store/collections/observations'
import type Observation from 'modules/payroll_bundle/store/models/Observation'

import type ObservationForm from '../../Forms/ObservationForm'

interface Props {
  onClose: () => void
  employee: Employee
  form: ObservationForm
  savedObservation?: Observation
}

const ObservationModal = ({ onClose, form, employee, savedObservation }: Props) => {
  const [disabled, setDisabled] = React.useState(false)

  const { supplementPermissions } = useStatusAuthorizations()

  const observationText = savedObservation?.get('observation_text')

  useUnsafeEffect(() => {
    if (observationText) {
      form.get('observation_text').set(observationText)
    }
  }, [form, observationText])

  const onSave = async () => {
    setDisabled(true)

    const formData = form.handoffData
    const observationData = { ...formData, effective_on: form.get('effective_on').value }
    const observation_text = formData.observation_text

    const action = savedObservation
      ? savedObservation.save({ observation_text })
      : observations.create(observationData)

    await asyncAction(() => action, {
      successFlash: true,
    })
    setDisabled(false)
    onClose()
  }

  const closeModal = () => {
    form.get('observation_text').set(form.get('observation_text').originalValue)
    onClose()
  }

  const canEditEmployeeObservation =
    supplementPermissions[employee.attributes.id]?.canEditEmployeeObservation

  return (
    <Modal
      title={i18n.t('payroll_bundle.supplements.observations_modal.title')}
      description={i18n.t('payroll_bundle.supplements.observations_modal.description', {
        fullName: employeeUser(employee).fullName(),
      })}
      illustration="document-edit"
      onClose={closeModal}
      footer={
        <Button onClick={onSave} type="primary" disabled={disabled || !canEditEmployeeObservation}>
          {i18n.t('payroll_bundle.supplements.observations_modal.submit')}
        </Button>
      }
    >
      <TextArea
        mandatory={false}
        readonly={disabled || !canEditEmployeeObservation}
        placeholder={i18n.t('payroll_bundle.supplements.observations_modal.placeholder')}
        field={form.get('observation_text')}
      />
    </Modal>
  )
}

export default observer(ObservationModal)
